import { useCallback, useMemo, useRef } from 'react';
import { ChartScatter } from '@signozhq/icons';
import {
	type DashboardtypesScatterPlotPanelSpecDTO,
	DashboardtypesThresholdAxisDTO,
} from 'api/generated/services/sigNoz.schemas';
import { useIsDarkMode } from 'hooks/useDarkMode';
import { useResizeObserver } from 'hooks/useDimensions';
import type { IRenderTooltipFooterArgs } from 'lib/uPlotV2/components/types';
import type { ScatterPointLabel } from 'lib/uPlotV2/plugins/ScatterPlugin/types';
import type { ScatterPointClick } from 'lib/visualization/charts/types';
import Scatter from 'lib/visualization/charts/Scatter/Scatter';
import {
	buildScatterConfig,
	prepareScatterChartData,
} from 'lib/visualization/charts/Scatter/utils';
import TooltipFooter from 'lib/visualization/panels/components/TooltipFooter';

import NoData from '../../components/NoData/NoData';
import PanelMessage from '../../components/PanelMessage/PanelMessage';
import PanelStyles from '../../panel.module.scss';
import type { PanelRendererProps } from '../../types/rendererProps';
import { prepareScatterClickData } from '../../utils/drilldown/prepareScatterClickData';
import { getBuilderQueries } from '../../utils/getBuilderQueries';
import { getPanelTimeRange } from '../../utils/getPanelTimeRange';
import {
	resolveDecimalPrecision,
	resolveLegendPosition,
} from '../../utils/chartAppearance/resolvers';
import {
	resolveSelectionPreferencesSource,
	shouldSaveSelectionPreference,
} from '../../utils/selectionPreferences';

import { ScatterPlotDataStatus } from './types';
import { getAxisThresholds, toScatterAxisOptions } from './utils/axisOptions';
import { findGroupByMismatch } from './utils/groupByMismatch';
import { getScatterPlotData } from './utils/getScatterPlotData';
import { getScatterPlotEmptyMessage } from './utils/messages';
import { resolvePointOpacity, resolvePointSize } from './utils/points';

import styles from './Renderer.module.scss';

function ScatterPlotPanelRenderer({
	panelId,
	panel,
	data,
	isFetching,
	refetch,
	panelMode,
	onClick,
	enableDrillDown,
}: PanelRendererProps<'signoz/ScatterPlotPanel'>): JSX.Element {
	const graphRef = useRef<HTMLDivElement>(null);
	const containerDimensions = useResizeObserver(graphRef);
	const isDarkMode = useIsDarkMode();

	const spec = useMemo<DashboardtypesScatterPlotPanelSpecDTO>(
		() => panel.spec.plugin.spec,
		[panel.spec.plugin.spec],
	);

	const scatterData = useMemo(
		() => getScatterPlotData(data, spec),
		[data, spec],
	);

	const decimalPrecision = useMemo(
		() => resolveDecimalPrecision(spec.formatting?.decimalPrecision),
		[spec.formatting?.decimalPrecision],
	);

	const legendPosition = useMemo(
		() => resolveLegendPosition(spec.legend?.position),
		[spec.legend?.position],
	);

	const builderQueries = useMemo(
		() => getBuilderQueries(panel.spec.queries || []),
		[panel.spec.queries],
	);

	const groupByMismatch = useMemo(
		() => findGroupByMismatch(builderQueries),
		[builderQueries],
	);

	const emptyMessage = getScatterPlotEmptyMessage(scatterData, groupByMismatch);

	const readyData =
		scatterData.status === ScatterPlotDataStatus.Ready &&
		scatterData.drawnGroups > 0
			? scatterData
			: undefined;

	const config = useMemo(
		() =>
			readyData
				? buildScatterConfig({
						id: panelId,
						series: readyData.series,
						isDarkMode,
						x: toScatterAxisOptions(
							spec.axes?.x,
							readyData.channels.x.unit,
							getAxisThresholds(spec.thresholds, DashboardtypesThresholdAxisDTO.x),
						),
						y: toScatterAxisOptions(
							spec.axes?.y,
							readyData.channels.y.unit,
							getAxisThresholds(spec.thresholds, DashboardtypesThresholdAxisDTO.y),
						),
						pointSize: resolvePointSize(spec.chartAppearance?.points),
						fillOpacity: resolvePointOpacity(spec.chartAppearance?.points),
						colorMapping: spec.legend?.customColors ?? {},
						decimalPrecision,
						selectionPreferencesSource: resolveSelectionPreferencesSource(panelMode),
						shouldSaveSelectionPreference: shouldSaveSelectionPreference(panelMode),
					})
				: undefined,
		[
			readyData,
			panelId,
			isDarkMode,
			spec.axes?.x,
			spec.axes?.y,
			spec.chartAppearance?.points,
			spec.legend?.customColors,
			spec.thresholds,
			decimalPrecision,
			panelMode,
		],
	);

	const chartData = useMemo(
		() => (readyData ? prepareScatterChartData(readyData.series) : undefined),
		[readyData],
	);

	const resolvePointGroupValues = useCallback(
		(seriesIndex: number, dataIndex: number): ScatterPointLabel[] =>
			readyData?.pointLabels[seriesIndex - 1]?.[dataIndex] ?? [],
		[readyData],
	);

	const handlePointClick = useCallback(
		({ seriesIndex, dataIndex, color, coordinates }: ScatterPointClick): void => {
			if (!onClick || !readyData) {
				return;
			}
			const payload = prepareScatterClickData({
				labels: readyData.pointLabels[seriesIndex - 1]?.[dataIndex] ?? [],
				label: readyData.series[seriesIndex - 1]?.label ?? '',
				color,
				axisQueries: readyData.axisQueries,
				builderQueries,
				coordinates,
				timeRange: getPanelTimeRange(data.requestPayload),
			});
			if (payload) {
				onClick(payload);
			}
		},
		[onClick, readyData, builderQueries, data.requestPayload],
	);

	const renderTooltipFooter = useCallback(
		({ isPinned, dismiss }: IRenderTooltipFooterArgs) => (
			<TooltipFooter
				id={panelId}
				isPinned={isPinned}
				dismiss={dismiss}
				canDrilldown={!!enableDrillDown}
			/>
		),
		[panelId, enableDrillDown],
	);

	return (
		<div
			data-testid="scatter-plot-panel-renderer"
			className={PanelStyles.panelContainer}
		>
			{!readyData && emptyMessage && (
				<PanelMessage
					icon={<ChartScatter size={18} />}
					title={emptyMessage.title}
					description={emptyMessage.description}
					data-testid="scatter-plot-empty-message"
				/>
			)}
			{!readyData && !emptyMessage && (
				<NoData isFetching={isFetching} onRetry={refetch} panel={panel} />
			)}
			{/* Stays mounted through the empty states: the size observer is bound to this node. */}
			<div
				ref={graphRef}
				className={styles.chart}
				hidden={!readyData}
				data-testid="scatter-plot-chart-area"
			>
				{readyData &&
					config &&
					chartData &&
					containerDimensions.width > 0 &&
					containerDimensions.height > 0 && (
						<Scatter
							key={panelId}
							config={config}
							data={chartData}
							channels={readyData.channels}
							resolvePointGroupValues={resolvePointGroupValues}
							legendConfig={{ position: legendPosition }}
							decimalPrecision={decimalPrecision}
							canPinTooltip
							width={containerDimensions.width}
							height={containerDimensions.height}
							renderTooltipFooter={renderTooltipFooter}
							onPointClick={enableDrillDown ? handlePointClick : undefined}
							data-testid="scatter-plot-chart"
						/>
					)}
			</div>
		</div>
	);
}

export default ScatterPlotPanelRenderer;
